import React, { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE = 'button, [href], [tabindex]:not([tabindex="-1"])';

/**
 * Full-screen screenshot viewer shared by both views.
 * Controlled component: `index` (null = closed) selects images[index]; the
 * parent owns the state and receives onClose() / onNavigate(nextIndex).
 * Keyboard: Esc closes, arrow keys navigate (wrapping), Tab stays inside.
 * Styled by .lightbox* in styles/app.css, skinned per view.
 */
const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const count = Array.isArray(images) ? images.length : 0;
  const open = count > 0 && index != null && index >= 0 && index < count;
  const dialogRef = useRef(null);

  const prev = useCallback(() => onNavigate((index - 1 + count) % count), [index, count, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % count), [index, count, onNavigate]);

  // Lock page scrolling and move focus into the dialog while it is open.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('lightbox-open');
    if (dialogRef.current) dialogRef.current.focus();
    return () => document.body.classList.remove('lightbox-open');
  }, [open]);

  // Keyboard handling.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const dialog = dialogRef.current;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && count > 1) {
        next();
      } else if (e.key === 'ArrowLeft' && count > 1) {
        prev();
      } else if (e.key === 'Tab' && dialog) {
        const items = dialog.querySelectorAll(FOCUSABLE);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === dialog)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, count, onClose, next, prev]);

  if (!open) return null;
  const img = images[index];

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot viewer"
      tabIndex={-1}
      ref={dialogRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <button type="button" className="lightbox-btn lightbox-close" aria-label="Close" onClick={onClose}>×</button>
      {count > 1 && (
        <button type="button" className="lightbox-btn lightbox-prev" aria-label="Previous screenshot" onClick={prev}>‹</button>
      )}
      <figure className="lightbox-figure">
        <img src={img.src} alt={img.alt} />
        <figcaption className="lightbox-caption">
          <span className="lightbox-count">{index + 1} / {count}</span>
          <span className="lightbox-alt">{img.alt}</span>
        </figcaption>
      </figure>
      {count > 1 && (
        <button type="button" className="lightbox-btn lightbox-next" aria-label="Next screenshot" onClick={next}>›</button>
      )}
    </div>
  );
};

export default Lightbox;
